import WeightChart from "@/components/ui/WeightChart.jsx";
import useQuizStore from "@/store/useQuizStore";

const SummaryStep = ({ step }) => {
  const { quizData, preferredUnit } = useQuizStore();

  const renderContent = () => {
    switch (step.content) {
      case "bacteriaOvergrowth":
        return <BacteriaOvergrowth />;
      case "bacteriaBalance":
        return <BacteriaBalance />;
      case "personalSummary":
        return <PersonalSummary />;
      case "gutWeightLoss":
        return <GutWeightLoss />;
      case "productBenefits":
        return <ProductBenefits />;
      case "howQuickly":
        return <HowQuickly />;
      case "testimonialSingle":
        return <TestimonialSingle />;
      case "testimonialMultiple":
        return <TestimonialMultiple />;
      default:
        return <p>Content not found: {step.content}</p>;
    }
  };

  return (
    <div className="animate-fade-in pb-16 md:pb-[65px]">{renderContent()}</div>
  );
};

function getWeightValues(quizData, preferredUnit) {
  const unit = preferredUnit || "imperial";

  if (unit === "metric") {
    return {
      currentWeight: parseFloat(quizData.currentWeightKg) || 110,
      goalWeight: parseFloat(quizData.goalWeightKg) || 65,
      unit: "kg",
    };
  } else {
    return {
      currentWeight: parseFloat(quizData.currentWeightLb) || 242,
      goalWeight: parseFloat(quizData.goalWeightLb) || 143,
      unit: "lb",
    };
  }
}

// Step 16: Bacteria Overgrowth
function BacteriaOvergrowth() {
  const { quizData, preferredUnit } = useQuizStore();
  const { currentWeight, goalWeight, unit } = getWeightValues(
    quizData,
    preferredUnit,
  );

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-title text-[1.6rem] md:text-[2.1rem] font-normal leading-[1.25] md:leading-[1.2] text-center text-main">
        You may have an{" "}
        <span className="text-red">overgrowth of bad bacteria in your gut</span>
      </h2>
      <p className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.55] text-center text-gray mb-2 md:mb-4">
        You can turn this around — improving your gut balance may help you lose
        weight more easily. Check your personal summary below to see how we can
        help.
      </p>

      <div className="bg-white rounded-2xl md:rounded-[22px] p-3.5 md:p-5 text-center">
        <h3 className="text-[0.8rem] md:text-[0.875rem] font-medium text-gray mb-2 text-center">
          Your weight loss rate
        </h3>
        <WeightChart
          currentWeight={currentWeight}
          goalWeight={goalWeight}
          unit={unit}
        />

        <div className="flex gap-4 justify-center mt-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-1 rounded-sm bg-green" />
            <span className="text-[0.7rem] md:text-[0.875rem] text-main">
              With Naori
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-1 rounded-sm bg-red" />
            <span className="text-[0.7rem] md:text-[0.875rem] text-main">
              With dieting
            </span>
          </div>
        </div>
      </div>

      <p className="text-[0.75rem] md:text-[0.875rem] text-gray text-center leading-[1.43] md:leading-[1.5] mt-1">
        Results are not guaranteed. Users may lose{" "}
        {unit === "kg" ? "0.5–1 kg" : "1–2 lb"} per week when following a
        healthy lifestyle. This formula is designed to make the process faster
        and easier.{" "}
      </p>
    </div>
  );
}

// Step 17: Bacteria Balance
function BacteriaBalance() {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-title text-[1.6rem] md:text-[2.1rem] font-normal leading-[1.25] md:leading-[1.2] text-center text-main">
        Your bacteria balance is <span className="text-red">weakened</span>
      </h2>
      <p className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.55] text-center text-gray mb-2 md:mb-4">
        Your quiz results suggest excess weight and a higher metabolic age —
        both potential signs of gut imbalance.
      </p>

      <div className="bg-white rounded-2xl md:rounded-[22px] p-3.5 md:p-5 text-center mb-2">
        <h3 className="text-[0.8rem] md:text-[0.875rem] font-medium text-gray mb-2 text-center">
          Your bacteria balance
        </h3>
        <div className="font-title text-5xl md:text-5xl font-normal leading-none my-2 text-center text-[#EF6D38]">
          32%
        </div>
        <p className="text-[0.75rem] md:text-[0.8rem] text-gray text-center">
          Status: <span className="text-[#EF6D38] font-medium">Weakened</span>
        </p>
        <div className="w-full p-0 mt-2">
          <img
            src={`${window.template_root}/src/public/img/scale-bacteria-balance3.svg`}
            alt="Bacteria Balance Scale"
            className="w-full h-auto"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
        <div className="bg-white rounded-2xl p-4 md:p-6 flex flex-col">
          <h4 className="text-[0.9rem] font-semibold mb-1 md:mb-3 text-red">
            Weight Gain Zone
          </h4>
          <ul className="list-none p-0 m-0 flex flex-col gap-2">
            <li className="text-[0.9rem] md:text-[0.99rem] text-main leading-[1.4]">
              Hunger and cravings
            </li>
            <li className="text-[0.9rem] md:text-[0.99rem] text-main leading-[1.4]">
              Excess fat storage
            </li>
            <li className="text-[0.9rem] md:text-[0.99rem] text-main leading-[1.4]">
              Brain fog and low energy
            </li>
            <li className="text-[0.9rem] md:text-[0.99rem] text-main leading-[1.4]">
              Bad bacteria overgrowth
            </li>
          </ul>
        </div>
        <div className="bg-white rounded-2xl p-4 md:p-6 flex flex-col">
          <h4 className="text-[0.9rem] font-semibold mb-1 md:mb-3 text-main">
            Weight Loss Zone
          </h4>
          <ul className="list-none p-0 m-0 flex flex-col gap-2">
            <li className="text-[0.9rem] md:text-[0.99rem] text-main leading-[1.4]">
              Better appetite control
            </li>
            <li className="text-[0.9rem] md:text-[0.99rem] text-main leading-[1.4]">
              Increased fat-burning rate
            </li>
            <li className="text-[0.9rem] md:text-[0.99rem] text-main leading-[1.4]">
              More energy & sharp focus
            </li>
            <li className="text-[0.9rem] md:text-[0.99rem] text-main leading-[1.4]">
              Healthy bacteria balance
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// Helper function to calculate BMI
function calculateBMI(quizData, preferredUnit) {
  const unit = preferredUnit || "metric";

  if (unit === "metric") {
    const weightKg = parseFloat(quizData.currentWeightKg);
    const heightM = parseFloat(quizData.heightCm) / 100;

    if (!weightKg || !heightM || heightM === 0) {
      return null;
    }

    // Formula: Weight (kg) / [Height (m)]²
    return weightKg / (heightM * heightM);
  } else {
    // Imperial (US)
    const weightLb = parseFloat(quizData.currentWeightLb);
    const heightFt = parseFloat(quizData.heightFt) || 0;
    const heightIn = parseFloat(quizData.heightIn) || 0;

    // Calculate total inches
    const totalInches = heightFt * 12 + heightIn;

    if (!weightLb || !totalInches || totalInches === 0) {
      return null;
    }

    // Formula: [Weight (lbs) / Height (in)²] x 703
    return (weightLb / (totalInches * totalInches)) * 703;
  }
}

// Helper function to get BMI category
function getBMICategory(bmi) {
  if (bmi < 24) {
    return {
      label: "Normal Weight",
      image: "img/scale-ok.svg",
      color: "#2E7E6D",
    };
  } else if (bmi < 30) {
    return {
      label: "Overweight",
      image: "img/scale-overweight.svg",
      color: "#F06815",
    };
  } else {
    return {
      label: "Obese",
      image: "img/scale-obese.svg",
      color: "#E6442F",
    };
  }
}

// Helper function to calculate metabolic age
function calculateMetabolicAge(quizData) {
  const age = parseInt(quizData.age);
  if (!age) return 42;

  const metabolicAge = age + 15;
  return Math.max(metabolicAge, 42);
}

// Step 18: Personal Summary
function PersonalSummary() {
  const { quizData, preferredUnit } = useQuizStore();
  const bmiValue = calculateBMI(quizData, preferredUnit);
  const bmi = bmiValue ? bmiValue.toFixed(2) : "31.92";
  const bmiCategory = getBMICategory(bmiValue || 31.92);
  const metabolicAge = calculateMetabolicAge(quizData);

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-title text-[1.6rem] md:text-[2.1rem] font-normal leading-[1.25] md:leading-[1.2] text-center text-main">
        Your personal summary{" "}
      </h2>
      <p className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.55] text-center text-gray mb-2 md:mb-4">
        Your results point to weight gain and slowed metabolism, which may
        improve with better gut health.{" "}
      </p>

      {/* BMI Section */}
      <div className="bg-white rounded-2xl md:rounded-[22px] p-3.5 md:p-5 text-center mb-2">
        <h3 className="text-[0.8rem] md:text-[0.875rem] font-medium text-gray mb-2 text-center">
          Your BMI{" "}
        </h3>
        <div
          className="font-title text-5xl md:text-5xl font-normal leading-none my-2 text-center"
          style={{ color: bmiCategory.color }}
        >
          {bmi}
        </div>
        <p className="text-[0.75rem] md:text-[0.8rem] text-gray text-center">
          <span style={{ color: bmiCategory.color }} className="font-medium">
            {bmiCategory.label}
          </span>
        </p>
        <div className="w-full p-0 mt-2">
          <img
            src={`${window.template_root}/src/public/${bmiCategory.image}`}
            alt="BMI Scale"
            className="w-full h-auto"
          />
          <div className="flex justify-around mt-1">
            <span className="text-[0.75rem] md:text-[0.8rem] text-gray text-center flex-1">
              Healthy
            </span>
            <span className="text-[0.75rem] md:text-[0.8rem] text-gray text-center flex-1">
              Overweight
            </span>
            <span className="text-[0.75rem] md:text-[0.8rem] text-gray text-center flex-1">
              Obese
            </span>
          </div>
        </div>
      </div>

      {/* Metabolic Age Section */}
      <div className="bg-white rounded-2xl md:rounded-[22px] p-3.5 md:p-5 text-center">
        <h3 className="text-[0.8rem] md:text-[0.875rem] font-medium text-gray mb-2 text-center">
          Your Metabolic Age{" "}
        </h3>
        <div className="font-title text-5xl md:text-5xl font-normal leading-none my-2 text-center text-[#F0721D]">
          {metabolicAge} years
        </div>
        <p className="text-[0.75rem] md:text-[0.8rem] text-gray text-center">
          Your age is{" "}
          <span className="text-[#EF6D38] font-medium">47% below average</span>
        </p>
        <p className="text-[0.9rem] md:text-base text-main my-4 leading-[1.4] text-center">
          Bad bacteria might be causing your body to hold on to fat.{" "}
        </p>

        <div className="bg-red/5 rounded-xl md:rounded-xl p-2.5 md:p-4 flex items-center justify-center gap-2 md:gap-2.5">
          <img
            src={`${window.template_root}/src/public/img/warning.svg`}
            alt="Warning"
            className="w-5 h-5 shrink-0"
          />
          <span className="text-[0.9rem] md:text-base font-medium text-red flex-1 text-left">
            Your fat-burning rate is very low
          </span>
        </div>
      </div>
    </div>
  );
}

// Step 19: Gut & Weight Loss
function GutWeightLoss() {
  const effects = [
    {
      icon: "hunger.avif",
      text: "May lead to <strong>persistent hunger and cravings</strong>, which can override your appetite control and lead to overeating.",
    },
    {
      icon: "fat-storage.avif",
      text: "Linked to <strong>excess fat storage</strong>, especially in the belly area, due to impaired metabolic processing.",
    },
    {
      icon: "brain-fog.avif",
      text: "Can cause <strong>brain fog and low energy</strong>, reducing your ability to stay active and focused throughout the day.",
    },
    {
      icon: "bad-bacteria.avif",
      text: "Encourages <strong>bad bacteria overgrowth</strong>, which throws off your gut balance and makes it harder to burn fat.",
    },
    {
      icon: "bloating.avif",
      text: "Associated with <strong>sluggish digestion and bloating</strong>, leading to discomfort, irregularity, and a constant heavy feeling.",
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-title text-[1.6rem] md:text-[2.1rem] font-normal leading-[1.25] md:leading-[1.2] text-center text-main">
        How does your gut bacteria affect weight loss?
      </h2>
      <p className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.55] text-center text-gray mb-2 md:mb-4">
        Too much bad bacteria may fuel inflammation and disrupt metabolism —
        both of which make weight loss more difficult.
      </p>

      <div className="bg-white rounded-2xl md:rounded-[22px] p-3.5 md:p-5 text-left mb-2">
        <h3 className="text-[0.8rem] md:text-[0.875rem] font-medium text-gray mb-2 text-center">
          Your metabolism
        </h3>
        <div className="font-title text-5xl md:text-5xl font-normal leading-none my-2 text-center text-[#F06815]">
          Slow
        </div>
        <p className="text-[0.9rem] md:text-base text-main mt-4 leading-[1.4] text-center">
          You have elevated gut bacteria levels, which may make weight loss
          harder.
        </p>
        <div className="w-full pt-3 md:pt-3">
          <img
            src={`${window.template_root}/src/public/img/naori-metabolism.svg`}
            alt="Metabolism Scale"
            fetchPriority="high"
            className="w-full h-auto"
          />
          <div className="flex justify-around mt-1">
            <span className="text-[0.75rem] md:text-[0.8rem] text-gray text-center flex-1">
              Very slow
            </span>
            <span className="text-[0.75rem] md:text-[0.8rem] text-gray text-center flex-1">
              Slow
            </span>
            <span className="text-[0.75rem] md:text-[0.8rem] text-gray text-center flex-1">
              Fast
            </span>
            <span className="text-[0.75rem] md:text-[0.8rem] text-gray text-center flex-1">
              Very fast
            </span>
          </div>
        </div>
      </div>

      <ul className="list-none flex flex-col gap-2 md:gap-2.5 p-0">
        {effects.map((effect, index) => (
          <li
            key={index}
            className="flex items-center gap-3 md:gap-4 p-3.5 md:p-4 rounded-[14px] md:rounded-[18px] bg-white"
          >
            <img
              src={`${window.template_root}/src/public/img/illustrations/${effect.icon}`}
              alt=""
              className="w-[58px] md:w-[62px] h-auto shrink-0 ml-0.5"
            />
            <span
              className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.5] text-main"
              dangerouslySetInnerHTML={{ __html: effect.text }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// Step 20: Product Benefits
function ProductBenefits() {
  const benefits = [
    {
      icon: "gut-composition.avif",
      text: "<strong>Boosts unique gut composition</strong>, instead of stuffing you with alienated good bacteria",
    },
    {
      icon: "prebiotics-probiotics.avif",
      text: "Combines <strong>prebiotics, probiotics and postbiotics</strong> that work together to elevate your gut health.",
    },
    {
      icon: "delayed-release.avif",
      text: "Uses <strong>delayed-release capsules</strong> that help the ingredients survive harsh stomach acidity.",
    },
    {
      icon: "freeze-dried.avif",
      text: "Contains <strong>freeze-dried cultures</strong> that resist heat, moisture, and time without breaking down.",
    },
    {
      icon: "dark-glass-bottle.avif",
      text: "Protected in <strong>dark glass bottles</strong> that block UV light and help preserve bacterial potency.",
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-title text-[1.6rem] md:text-[2.1rem] font-normal leading-[1.25] md:leading-[1.2] text-center text-main">
        We've got you! Naori is designed to{" "}
        <span className="text-light-green">restore your gut microbiome</span>
      </h2>
      <p className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.55] text-center text-gray mb-2 md:mb-4">
        Naori works to restore your gut health — laying the foundation for
        easier, more sustainable weight loss.
      </p>

      <img
        src={`${window.template_root}/src/public/img/product.avif`}
        alt="Naori Capsules"
        fetchPriority="high"
        className="w-full h-auto rounded-2xl md:rounded-[22px] mb-0 md:mb-2"
      />

      <ul className="list-none flex flex-col gap-2 md:gap-2.5 p-0">
        {benefits.map((benefit, index) => (
          <li
            key={index}
            className="flex items-center gap-3 md:gap-4 p-3.5 md:p-4 rounded-[14px] md:rounded-[18px] bg-white"
          >
            <img
              src={`${window.template_root}/src/public/img/illustrations/${benefit.icon}`}
              alt=""
              fetchPriority="high"
              className="w-[58px] md:w-[62px] h-auto shrink-0 ml-0.5"
            />
            <span
              className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.5] text-main"
              dangerouslySetInnerHTML={{ __html: benefit.text }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// Step 21: How Quickly
function HowQuickly() {
  const timelineSteps = [
    {
      title: "Less Bloating & Better Digestion",
      description:
        "Your stomach feels lighter and less uncomfortable after meals. Bloating starts to fade. Your bathroom trips become more regular. You notice you're not as sluggish after eating.",
    },
    {
      title: "More Energy & Reduced Cravings",
      description:
        "You wake up feeling refreshed and energized. That afternoon crash disappears. You stop reaching for snacks between meals. Your appetite feels more balanced and under control.",
    },
    {
      title: "Flatter Stomach & Weight Loss Begins",
      description:
        "Your pants fit looser. Your stomach looks noticeably flatter. You're eating less without feeling hungry. The scale starts moving in the right direction. People ask if you're doing something different.",
    },
    {
      title: "Full Energy & Confidence Back",
      description:
        "You feel amazing inside and out. Steady energy all day long. Your skin looks clearer. You've dropped weight without suffering. You finally feel like yourself again - or better than ever.",
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-title text-[1.6rem] md:text-[2.1rem] font-normal leading-[1.25] md:leading-[1.2] text-center text-main">
        Wondering <span className="text-light-green">how quickly</span> Naori
        can make a difference?
      </h2>
      <p className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.55] text-center text-gray mb-2 md:mb-4">
        Naori targeted formula begins working from day one, jumpstarting your
        path to a lighter, healthier you.
      </p>

      <img
        src={`${window.template_root}/src/public/img/hand.avif`}
        alt="Naori Capsules in Hand"
        fetchPriority="high"
        className="w-full h-auto rounded-2xl md:rounded-[22px] mb-0"
      />

      {/* Instruction Blocks */}
      <div className="grid grid-cols-2 gap-2 md:gap-4 mb-0">
        <div className="bg-white rounded-[14px] md:rounded-[18px] p-4 md:p-6 text-center flex flex-col items-center gap-2 md:gap-3">
          <img
            src={`${window.template_root}/src/public/img/illustrations/two-capsules.avif`}
            alt="Capsule"
            className="w-[70px] md:w-[90px] h-auto object-contain"
          />
          <div className="font-semibold text-main leading-[1.35] md:leading-[1.4] text-[0.85rem] md:text-base">
            <span className="text-light-green">Take 2 capsules</span> before
            breakfast
          </div>
        </div>
        <div className="bg-white rounded-[14px] md:rounded-[18px] p-4 md:p-6 text-center flex flex-col items-center gap-2 md:gap-3">
          <img
            src={`${window.template_root}/src/public/img/illustrations/healthy-gut.avif`}
            alt="Gut Health"
            className="w-[70px] md:w-[90px] h-auto object-contain"
          />
          <div className="font-semibold text-main leading-[1.35] md:leading-[1.4] text-[0.85rem] md:text-base">
            <span className="text-light-green">Healthy gut</span> & steady
            weight loss
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-2xl md:rounded-[22px] p-3.5 md:p-5">
        <div className="flex flex-col gap-3 md:gap-4 text-left">
          {timelineSteps.map((step, index) => (
            <div
              key={index}
              className="flex items-start gap-3 md:gap-4 relative"
            >
              {/* Connector line */}
              {index < timelineSteps.length - 1 && (
                <div className="absolute left-[14px] top-[35px] md:top-[35px] w-0.5 h-[calc(100%+0.8rem-36px)] md:h-[calc(100%+1rem-42px)] bg-background z-[1]" />
              )}

              {/* Step number */}
              <div className="bg-[#CCFF70] text-main font-semibold text-[0.9rem] w-7 h-7 md:w-[30px] md:h-[30px] rounded-[10px] flex items-center justify-center shrink-0 -mt-0.5 relative z-[2]">
                {index + 1}
              </div>

              {/* Step content */}
              <div className="flex-1">
                <h4 className="text-[0.95rem] md:text-base font-semibold text-green m-0 mb-1 md:mb-2 mt-[0.14rem] leading-[1.3]">
                  {step.title}
                </h4>
                <p className="text-[0.9rem] md:text-[0.95rem] text-gray m-0 leading-[1.4] md:leading-[1.5]">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-accent-bg rounded-[14px] md:rounded-[18px] p-3.5 md:p-5 text-center mt-1">
        <img
          src={`${window.template_root}/src/public/img/benefits.svg`}
          alt="Product Benefits Icons"
          className="w-full h-auto mx-auto block"
        />
      </div>
    </div>
  );
}

// Step 22: Single Testimonial
function TestimonialSingle() {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 md:gap-4 mx-auto max-w-full mb-0">
        <div className="flex-1 relative">
          <img
            src={`${window.template_root}/src/public/img/before.avif`}
            alt="Before Results"
            fetchPriority="high"
            className="w-full h-auto block rounded-[14px] md:rounded-2xl object-cover"
          />
          <div className="absolute bottom-2 md:bottom-3 left-2 md:left-3 bg-white text-main px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg md:rounded-[10px] font-semibold text-[0.8rem] md:text-[0.9rem]">
            Before
          </div>
        </div>
        <div className="flex-1 relative">
          <img
            src={`${window.template_root}/src/public/img/after.avif`}
            alt="After Results"
            fetchPriority="high"
            className="w-full h-auto block rounded-[14px] md:rounded-2xl object-cover"
          />
          <div className="absolute bottom-2 md:bottom-3 left-2 md:left-3 bg-white text-main px-2.5 md:px-3 py-1 md:py-1.5 rounded-lg md:rounded-[10px] font-semibold text-[0.8rem] md:text-[0.9rem]">
            After
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[14px] md:rounded-[18px] p-4 md:p-6">
        <blockquote className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.45] m-0 mb-2.5 md:mb-4 text-main font-normal not-italic">
          <p className="mb-2.5 md:mb-3">
            Before Naori, I always felt bloated and uncomfortable. My stomach
            hurt after eating and I felt tired all the time.
          </p>
          <p className="mb-2.5 md:mb-3">
            Everything changed when I started Naori Synbiotics. After a few
            weeks, I felt so much better. Less bloating, better digestion, and
            way more energy every day.
          </p>
          <p>
            I can't remember the last time I felt this good. If your stomach
            bothers you every day, try Naori. You'll feel amazing.
          </p>
        </blockquote>

        <div className="flex items-center justify-between flex-wrap gap-3 md:gap-4">
          <span className="text-[0.8rem] md:text-[0.9rem] font-medium text-gray">
            Cyndia K.
          </span>
          <div className="flex items-center gap-1 md:gap-1.5 text-light-green text-[0.8rem] md:text-[0.9rem] font-medium">
            <img
              src={`${window.template_root}/src/public/img/verified.svg`}
              alt="Verified"
              className="w-3.5 h-3.5 md:w-[18px] md:h-[18px]"
            />
            <span>Verified Customer</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 23: Multiple Testimonials
function TestimonialMultiple() {
  const testimonials = [
    {
      name: "Brianna O.",
      quote:
        '"This really helped me with my weight issues. Lost 13 lbs that never came back!"',
      image: "testimonials1.avif",
      video: "01_AliveBlue_Hydrogen-bottle_20251215-200236.mp4",
    },
    {
      name: "Janelle G.",
      quote:
        '"Taking care of your gut is probably the best thing you can do for your weight loss."',
      image: "testimonials2.avif",
      video: "02_Demoldix_Mold%20Remover.mp4",
    },
    {
      name: "Kristie C.",
      quote:
        '"I wish I\'d found these sooner. Naori helped me achieve my weight goal and then some."',
      image: "testimonials3.avif",
      video: "01_Clairu_Mini-air-purifier_20251127-144050.mp4",
    },
    {
      name: "Mari M.",
      quote:
        '"I take two capsules a day and finally feel and look lighter. Lost 15 pounds too!"',
      image: "testimonials4.avif",
      video: "01_Hero_TellyStick_20251202.mp4",
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-title text-[1.6rem] md:text-[2.1rem] font-normal leading-tight md:leading-[1.2] text-center text-main">
        Thousands trust Naori.{" "}
        <span className="text-light-green">Here's what they're saying</span>
      </h2>

      <div className="flex flex-col gap-2 md:gap-2.5 my-2 md:my-4">
        {testimonials.map((testimonial, index) => (
          <div
            key={index}
            className="bg-white rounded-[14px] md:rounded-[18px] p-2.5 md:p-4"
          >
            <div className="flex gap-3 md:gap-4 items-center">
              <div className="w-[65px] md:w-[75px] h-[90px] md:h-[90px] rounded-md overflow-hidden shrink-0">
                {/*<div style={{ aspectRatio: "1 / 1", height: "100%" }}>
                  <video autoPlay playsInline muted loop style={{ width: "100%", height: "100%" }}>
                    <source src={videoSrc(testimonial.video)} type="video/mp4" />
                  </video>
                </div>*/}
                <img
                  src={`${window.template_root}/src/public/img/testimonials/${testimonial.image}`}
                  alt={`${testimonial.name} Testimonial`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 text-left">
                <blockquote className="text-[0.9rem] md:text-base leading-[1.4] md:leading-[1.45] m-0 mb-2.5 md:mb-4 text-main font-normal not-italic">
                  {testimonial.quote}
                </blockquote>
                <div className="flex items-center justify-between flex-wrap gap-3 md:gap-4">
                  <span className="text-[0.8rem] md:text-[0.9rem] font-medium text-gray">
                    {testimonial.name}
                  </span>
                  <div className="flex items-center gap-1 md:gap-1.5 text-light-green text-[0.8rem] md:text-[0.9rem] font-medium">
                    <img
                      src={`${window.template_root}/src/public/img/verified.svg`}
                      alt="Verified"
                      className="w-3.5 h-3.5 md:w-[18px] md:h-[18px]"
                    />
                    <span>Verified</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-accent-bg rounded-[14px] md:rounded-[18px] p-4 md:p-5 text-center">
        <p className="text-[0.8rem] md:text-[0.875rem] font-medium text-gray mb-3 md:mb-5 text-center">
          Probiotics were covered in
        </p>
        <img
          src={`${window.template_root}/src/public/img/covered.svg`}
          alt="Media Coverage Logos"
          className="w-full h-auto mx-auto block"
        />
      </div>
    </div>
  );
}

export default SummaryStep;
